import React, { useMemo } from 'react';
import { Form, Select } from 'antd';
import { trpc } from '../lib/api';

interface ParentEquipmentSelectProps {
  name?: string;
  label?: string;
  currentId?: string | null;
  allowClear?: boolean;
  placeholder?: string;
  extra?: string;
  onChange?: (value: string | null) => void;
  /** インラインモード（Form.Itemでラップせず直接Selectを返す） */
  inline?: boolean;
  style?: React.CSSProperties;
  value?: string | null;
}

interface EquipmentListItem {
  id: string;
  code?: string | null;
  name: string;
}

interface EquipmentListResponse {
  data?: EquipmentListItem[];
}

/**
 * 親設備選択用の共通フォーム項目。
 * - 一覧/詳細の編集モーダル、登録モーダルの双方で再利用可能。
 * - 自身の設備IDは選択肢から除外。
 */
export const ParentEquipmentSelect: React.FC<ParentEquipmentSelectProps> = ({
  name = 'parentId',
  label = '親設備',
  currentId = null,
  allowClear = true,
  placeholder = '親設備を選択（任意）',
  extra,
  onChange,
  inline = false,
  style,
  value,
}) => {
  // サーバ制約に合わせて pageSize は 100 まで
  const { data: equipmentData } = trpc.equipment.getList.useQuery({
    pagination: { page: 1, pageSize: 100 },
    filter: { isActive: true },
  });

  const options = useMemo(() => {
    const response = equipmentData as EquipmentListResponse | undefined;
    const items: EquipmentListItem[] = response?.data ?? [];
    return items
      .filter((equipment) => (currentId ? equipment.id !== currentId : true))
      .map((equipment) => ({
        value: equipment.id,
        label: `${equipment.code ?? ''} ${equipment.name}`.trim(),
      }));
  }, [equipmentData, currentId]);

  const selectElement = (
    <Select
      allowClear={allowClear}
      showSearch
      placeholder={placeholder}
      optionFilterProp="label"
      options={options}
      style={style}
      value={value}
      onChange={(val) => {
        onChange?.(val ?? null);
      }}
    />
  );

  if (inline) {
    return selectElement;
  }

  return (
    <Form.Item name={name} label={label} extra={extra}>
      {selectElement}
    </Form.Item>
  );
};

export default ParentEquipmentSelect;
